import { create } from 'zustand';
import type { Editor } from 'grapesjs';
import { autoNameLayers } from '../utils/grapes-layer-names';
import { useProjectsStore } from './projects-store';

type DeviceType = 'Desktop' | 'Tablet' | 'Mobile';

interface EditorState {
  editorInstance: Editor | null;
  currentDevice: DeviceType;
  isLoadingContent: boolean;

  setEditorInstance: (editor: Editor | null) => void;
  setDevice: (device: DeviceType) => void;
  loadContent: (content: string | { html?: string; css?: string; components?: unknown; styles?: unknown; projectData?: unknown }) => void;
  getExportData: () => { html: string; css: string };
  applyAppBackground: (backgroundColor?: string) => void;

  previewContent: { html: string; css: string } | null;
  setPreviewContent: (content: { html: string; css: string } | null) => void;

  floatingPanel: { isOpen: boolean; x: number; y: number };
  setFloatingPanel: (state: { isOpen: boolean; x: number; y: number }) => void;
}

// Helper to strip blocking external resources
const sanitize = (str: string) => {
  if (!str) return str;
  return str
    .replace(/<link[^>]*href="[^"]*cdnjs[^"]*"[^>]*>/g, '')
    .replace(/<link[^>]*href="[^"]*font-awesome[^"]*"[^>]*>/g, '')
    .replace(/<script[^>]*src="[^"]*cdnjs[^"]*"[^>]*><\/script>/g, '');
};

function ensureTailwind(editor: Editor) {
  const doc = editor.Canvas.getDocument();
  const win = editor.Canvas.getWindow() as Window & { tailwind?: { refresh?: () => void } };
  if (!doc || !win) return;

  // Force Tailwind to rescan the DOM after content change.
  // Tailwind Play CDN should already be loaded via canvas.scripts config.
  const triggerRefresh = () => {
    // Method 1: Direct Tailwind refresh (if exposed)
    if (typeof win.tailwind?.refresh === 'function') {
      win.tailwind.refresh();
      return;
    }

    // Method 2: Trigger MutationObserver by modifying DOM
    const body = doc.body;
    if (body) {
      // Clone all children to force a full DOM mutation
      const wrapper = doc.createElement('div');
      wrapper.id = '__tw-refresh__';
      wrapper.className = 'hidden';
      body.appendChild(wrapper);
      // Remove on next frame
      requestAnimationFrame(() => {
        wrapper.remove();
        // Also try changing an existing element's class to trigger observer
        const firstEl = body.querySelector('[class]');
        if (firstEl) {
          const original = firstEl.className;
          firstEl.className = original + ' ';
          requestAnimationFrame(() => {
            firstEl.className = original;
          });
        }
      });
    }
  };

  // Run refresh at multiple intervals to catch Tailwind after it processes
  setTimeout(triggerRefresh, 50);
  setTimeout(triggerRefresh, 200);
  setTimeout(triggerRefresh, 500);
}

function applyCanvasBackground(editor: Editor, backgroundColor?: string) {
  const color = (backgroundColor ?? '').trim();
  if (!/^#[0-9a-f]{6}$/i.test(color)) return;

  const doc = editor.Canvas.getDocument();
  if (!doc) return;
  try {
    doc.documentElement.style.backgroundColor = color;
    doc.body.style.backgroundColor = color;
  } catch {
    // ignore
  }
}

function safePageId(page: any): string {
  return String(page?.get?.('id') ?? page?.id ?? '');
}

function safePageName(page: any): string {
  return String(page?.get?.('name') ?? '');
}

function restoreSelectedPage(editor: any, selected: { id: string; name: string } | null) {
  if (!selected?.id && !selected?.name) return;
  if (!editor?.Pages?.getAll || !editor?.Pages?.select) return;

  try {
    const all = editor.Pages.getAll?.();
    const pages = Array.isArray(all) ? all : all?.models ?? [];
    const match =
      (selected.id ? pages.find((p: any) => safePageId(p) === selected.id) : null) ??
      (selected.name ? pages.find((p: any) => safePageName(p) === selected.name) : null);
    if (match) editor.Pages.select(match);
  } catch {
    // ignore
  }
}

export const useEditorStore = create<EditorState>()((set, get) => ({
  editorInstance: null,
  currentDevice: 'Desktop',
  previewContent: null,
  isLoadingContent: false,
  floatingPanel: { isOpen: false, x: 0, y: 0 },

  setEditorInstance: (editorInstance) => set({ editorInstance }),
  setPreviewContent: (previewContent) => set({ previewContent }),
  setFloatingPanel: (floatingPanel) => set({ floatingPanel }),

  applyAppBackground: (backgroundColor) => {
    const editor = get().editorInstance;
    if (!editor) return;
    applyCanvasBackground(editor, backgroundColor);
    // Deliberately do NOT override the generated root component background.
    // This setting is meant to control the canvas/app "chrome" background behind the design.
    // Overriding the root component can unintentionally wipe intended gradients/dark themes.
  },

  setDevice: (device) => {
    const editor = get().editorInstance;
    if (editor) {
      editor.setDevice(device);
    }
    set({ currentDevice: device });
  },

  loadContent: (content) => {
    const editor = get().editorInstance;
    if (!editor) return;

    // Preserve current page selection across loads.
    const selectedPage = (() => {
      try {
        const p = (editor as any)?.Pages?.getSelected?.();
        if (!p) return null;
        const id = safePageId(p);
        const name = safePageName(p);
        return { id, name };
      } catch {
        return null;
      }
    })();

    // Signal that we're bulk-loading content — auto-save and layers should ignore events
    set({ isLoadingContent: true });

    try {
      if (typeof content === 'string') {
        editor.setComponents(sanitize(content));
      } else {
        if (content.projectData && (editor as any).loadProjectData) {
          // Project data contains pages, symbols, etc. Use it when available.
          (editor as any).loadProjectData(content.projectData as any);
        } else {
          if (content.components) {
            editor.setComponents(content.components as any);
          } else if (content.html !== undefined) {
            editor.setComponents(sanitize(content.html));
          }

          if (content.styles) {
            editor.setStyle(content.styles as any);
          } else if (content.css !== undefined) {
            editor.setStyle(content.css);
          }
        }
      }

      // Restore selection ASAP to avoid "page 1 then current" flicker.
      restoreSelectedPage(editor as any, selectedPage);
      queueMicrotask(() => restoreSelectedPage(editor as any, selectedPage));
      requestAnimationFrame(() => restoreSelectedPage(editor as any, selectedPage));

      // Apply app background color (editor canvas) deterministically, independent of model output.
      const bg = useProjectsStore.getState().currentProject?.generationSettings?.backgroundColor;
      applyCanvasBackground(editor, bg);
      // See applyAppBackground(): we don't override the design root itself.

      // Give Layers tree meaningful labels (best-effort, deterministic).
      // Run after the bulk load so GrapesJS component tree is in place.
      setTimeout(() => {
        try {
          autoNameLayers(editor as any);
        } catch {
          // ignore
        }
      }, 0);

      ensureTailwind(editor);
    } finally {
      // Use setTimeout to let GrapesJS finish its internal event processing
      setTimeout(() => {
        set({ isLoadingContent: false });
      }, 0);
    }
  },

  getExportData: () => {
    const editor = get().editorInstance;
    if (!editor) return { html: '', css: '' };
    return {
      html: editor.getHtml() || '',
      css: editor.getCss() || '',
    };
  },
}));
